import type { AssertionKey, ValidationOperator } from './assertions';

export type AssertionRuleField =
  | 'status'
  | 'header'
  | 'body'
  | 'responseTimeMs'
  | 'responseSize'
  | 'contentType';
export type AssertionRuleOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'exists'
  | 'gt'
  | 'lt'
  | 'is_present'
  | 'is_not_empty'
  | 'is_boolean'
  | 'is_integer'
  | 'regex';
export type AssertionRuleKind = 'if' | 'assert';
export type AssertionRuleBranch = 'then' | 'else';
/** When iterating array items: all must match (AND) or at least one (OR). */
export type AssertionRuleArrayMatchMode = 'all' | 'any';

export type AssertionRuleAdditionalClause = {
  combineWithPrevious: 'and' | 'or';
  field: AssertionRuleField;
  operator: AssertionRuleOperator;
  path: string;
  value: string;
};

export type AssertionRule = {
  id: string;
  parentRuleId?: string | null;
  branch?: AssertionRuleBranch;
  enabled: boolean;
  ruleKind: AssertionRuleKind;
  /**
   * When set, this Rule Builder rule belongs only to that data set.
   * Omitting it is legacy request-scoped; new saves from a data-set context stamp this field.
   */
  dataSetId?: number | null;

  field?: AssertionRuleField;
  operator?: AssertionRuleOperator;
  path?: string;
  value?: string;
  additionalClauses?: AssertionRuleAdditionalClause[];
  negate?: boolean;

  /** Same keys as Add/Edit Assertion. Defaults to json_path when omitted (legacy rules). */
  assertAssertionKey?: AssertionKey;
  assertJsonPath?: string;
  /** Header name when assertAssertionKey is headers. */
  assertHeaderName?: string;
  assertOperator?: ValidationOperator;
  assertExpected?: string;
  /** NONE keeps the existing single-value IF/ASSERT behavior. OR uses expectedValues. */
  logicalOperator?: 'NONE' | 'OR' | string;
  /** Alternative expected/compare values when logicalOperator is OR. */
  expectedValues?: string[];
  failureMessage?: string;
  severity?: 'soft' | 'hard';
  /** When true, expand [*] in assertJsonPath across all array items. */
  iterateAll?: boolean;
  /** With [*] / iterateAll: `all` = every item must pass; `any` = at least one item must pass. */
  arrayMatchMode?: AssertionRuleArrayMatchMode;
  /**
   * When true: on assert PASS create/update a variable; on FAIL delete it if it exists.
   * Name defaults from the JSON path leaf unless manageVariableName is set.
   */
  manageVariableOnResult?: boolean;
  /** Explicit variable name (editable). Falls back to JSON path leaf when empty. */
  manageVariableName?: string;
  /** Variable scope for create/update/delete. Defaults to request. */
  manageVariableScope?: 'request' | 'collection';
  /**
   * When iterating: concrete parent item path used for variable extraction (e.g. `$.content[0]`).
   * UI inspection / variable source only — does not affect assertion evaluation.
   */
  manageVariableItemPath?: string;
  /**
   * Relative field path under manageVariableItemPath (e.g. `contactName` or `user.firstName`).
   * Combined with the item path to resolve the variable value from the latest response.
   */
  manageVariableFieldPath?: string;
};

export type AssertionRuleResult = {
  id: string;
  jsonPath: string;
  operator: string;
  expected: string;
  actual: string;
  passed: boolean;
  message?: string;
  severity: 'soft' | 'hard';
  /** Inactive IF/ELSE branch — listed for visibility but not executed. */
  skipped?: boolean;
  /** Rule Builder display metadata for assertions table hierarchy. */
  ruleKind?: AssertionRuleKind;
  branch?: AssertionRuleBranch;
  depth?: number;
  summary?: string;
  /** Source rule id in assertionRulesJson (for delete from assertions table). */
  ruleId?: string;
};

export function parseAssertionRulesJson(raw: string | null | undefined): AssertionRule[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AssertionRule[]) : [];
  } catch {
    return [];
  }
}

function normalizedRuleDataSetId(rule: AssertionRule): number | null {
  const id = rule.dataSetId;
  return id != null && Number(id) > 0 ? Number(id) : null;
}

/** Rules that should appear / evaluate for the active data-set (or request-only) context. */
export function filterAssertionRulesForDataSet(
  rules: AssertionRule[],
  dataSetId?: number | null
): AssertionRule[] {
  const viewDs = dataSetId != null && dataSetId > 0 ? dataSetId : null;
  if (viewDs == null) {
    return rules.filter((r) => normalizedRuleDataSetId(r) == null);
  }
  return rules.filter((r) => normalizedRuleDataSetId(r) === viewDs);
}

export function filterAssertionRulesJsonForDataSet(
  rulesJson: string | null | undefined,
  dataSetId?: number | null
): string {
  return JSON.stringify(filterAssertionRulesForDataSet(parseAssertionRulesJson(rulesJson), dataSetId));
}

/**
 * Rules to evaluate for one execution slot.
 * A data set runs only its own Rule Builder rules. Request-level (unscoped) rules
 * run only when no data set is selected. Another data set must not inherit them.
 */
export function resolveAssertionRulesJsonForDataSetExecution(
  rulesJson: string | null | undefined,
  dataSetId?: number | null
): string {
  return filterAssertionRulesJsonForDataSet(rulesJson, dataSetId);
}

/**
 * Rules shown in the Rule Builder editor for a data set — only that data set's rules.
 * Legacy unscoped rules are claimed via stamp/save onto a single data set (not shared).
 */
export function assertionRulesForDataSetEditor(
  rulesJson: string | null | undefined,
  dataSetId?: number | null
): AssertionRule[] {
  return filterAssertionRulesForDataSet(parseAssertionRulesJson(rulesJson), dataSetId);
}

/** Persist editor rules for one data set without wiping other data sets' rules. */
export function mergeAssertionRulesJsonForDataSet(
  allRulesJson: string | null | undefined,
  editedRules: AssertionRule[],
  dataSetId?: number | null
): string {
  const all = parseAssertionRulesJson(allRulesJson);
  const viewDs = dataSetId != null && dataSetId > 0 ? dataSetId : null;
  if (viewDs == null) {
    const keepScoped = all.filter((r) => normalizedRuleDataSetId(r) != null);
    const unscoped = editedRules.map((r) => {
      const next = { ...r };
      delete next.dataSetId;
      return next;
    });
    return JSON.stringify([...keepScoped, ...unscoped]);
  }
  const keepOtherDatasets = all.filter((r) => {
    const ds = normalizedRuleDataSetId(r);
    return ds != null && ds !== viewDs;
  });
  const editedIds = new Set(editedRules.map((r) => r.id));
  const stamped = editedRules.map((r) => ({ ...r, dataSetId: viewDs }));
  // Claim leftover legacy unscoped rules onto this data set on save (do not leave them shared).
  const claimedUnscoped = all
    .filter((r) => normalizedRuleDataSetId(r) == null && !editedIds.has(r.id))
    .map((r) => ({ ...r, dataSetId: viewDs }));
  return JSON.stringify([...keepOtherDatasets, ...stamped, ...claimedUnscoped]);
}

/**
 * Clone Rule Builder rules from one data set onto another (new ids, remapped parents).
 * If the source has no scoped rules, copies legacy unscoped rules onto the target only.
 */
export function cloneAssertionRulesJsonForDuplicatedDataSet(
  allRulesJson: string | null | undefined,
  fromDataSetId: number,
  toDataSetId: number
): string | null {
  if (!(fromDataSetId > 0) || !(toDataSetId > 0) || fromDataSetId === toDataSetId) {
    return null;
  }
  const all = parseAssertionRulesJson(allRulesJson);
  if (all.length === 0) return null;

  let source = filterAssertionRulesForDataSet(all, fromDataSetId);
  if (source.length === 0) {
    source = all.filter((r) => normalizedRuleDataSetId(r) == null);
  }
  if (source.length === 0) return null;

  const idMap = new Map<string, string>();
  for (const rule of source) {
    idMap.set(rule.id, newAssertionRuleId());
  }
  const cloned = source.map((rule) => ({
    ...rule,
    id: idMap.get(rule.id) || newAssertionRuleId(),
    parentRuleId: rule.parentRuleId ? idMap.get(rule.parentRuleId) ?? null : null,
    dataSetId: toDataSetId,
  }));
  return JSON.stringify([...all, ...cloned]);
}

/** Stamp legacy unscoped rules onto a data set (one-time ownership claim). */
export function stampUnscopedAssertionRulesToDataSet(
  rulesJson: string | null | undefined,
  dataSetId: number
): string | null {
  if (!(dataSetId > 0)) return null;
  const rules = parseAssertionRulesJson(rulesJson);
  if (rules.length === 0) return null;
  let changed = false;
  const next = rules.map((r) => {
    if (normalizedRuleDataSetId(r) != null) return r;
    changed = true;
    return { ...r, dataSetId };
  });
  return changed ? JSON.stringify(next) : null;
}

export function newAssertionRuleId(): string {
  return `ar_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Collect rule ids to remove including all descendants (parent IF deletes child tree). */
export function collectAssertionRuleIdsToRemove(
  rules: AssertionRule[],
  rootIds: string[]
): Set<string> {
  const toRemove = new Set(rootIds.filter((id) => id?.trim()));
  let changed = true;
  while (changed) {
    changed = false;
    for (const r of rules) {
      if (r.parentRuleId && toRemove.has(r.parentRuleId) && !toRemove.has(r.id)) {
        toRemove.add(r.id);
        changed = true;
      }
    }
  }
  return toRemove;
}

export function removeAssertionRulesByIds(
  rulesJson: string | null | undefined,
  rootIds: string[]
): string {
  const rules = parseAssertionRulesJson(rulesJson);
  if (rules.length === 0 || rootIds.length === 0) return rulesJson?.trim() ? rulesJson : '[]';
  const toRemove = collectAssertionRuleIdsToRemove(rules, rootIds);
  return JSON.stringify(rules.filter((r) => !toRemove.has(r.id)));
}

/** Legacy assert rules omit the key and behave as JSON Path. */
export function resolveAssertAssertionKey(rule: AssertionRule): AssertionKey {
  const key = (rule.assertAssertionKey || '').trim().toLowerCase();
  if (
    key === 'status_code' ||
    key === 'response_time' ||
    key === 'response_size' ||
    key === 'content_type' ||
    key === 'headers' ||
    key === 'json_path'
  ) {
    return key as AssertionKey;
  }
  return 'json_path';
}

/**
 * Map IF rule `field` (+ path) to the same Assertion Key used by Assert rules.
 * Accepts legacy field names and assertion-key aliases.
 */
export function resolveIfAssertionKey(
  field?: string | null,
  path?: string | null
): AssertionKey {
  const f = String(field || 'body').trim();
  const p = String(path || '').trim().toLowerCase();
  switch (f) {
    case 'status':
    case 'status_code':
      return 'status_code';
    case 'responseTimeMs':
    case 'response_time':
      return 'response_time';
    case 'responseSize':
    case 'response_size':
      return 'response_size';
    case 'contentType':
    case 'content_type':
      return 'content_type';
    case 'header':
    case 'headers':
      if (p === 'content-type') return 'content_type';
      return 'headers';
    case 'body':
    case 'json_path':
    default:
      return 'json_path';
  }
}

/** Store IF `field` using the Assert-rule Assertion Key selection. */
export function assertionKeyToIfField(key: AssertionKey): AssertionRuleField {
  switch (key) {
    case 'status_code':
      return 'status';
    case 'response_time':
      return 'responseTimeMs';
    case 'response_size':
      return 'responseSize';
    case 'content_type':
      return 'contentType';
    case 'headers':
      return 'header';
    case 'json_path':
    default:
      return 'body';
  }
}
